/**
 * @fileoverview TipTap `spreadsheetBlock` node — an atom block that stores a
 * SpreadsheetSpec (JSON in a data attribute) and renders it as a read-only
 * table via a React NodeView. Round-trips through the canvas `html` field so
 * spreadsheets saved to the canvas persist and re-render on load (and are
 * captured by the html2canvas PDF export, which converts the placeholder div
 * to a real `<table>`).
 */
import { Node, mergeAttributes } from "@tiptap/core";
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from "@tiptap/react";
import type { SpreadsheetSpec } from "@cvx/lib/spreadsheets/spec";
import {
  encodeSpreadsheetAttr,
  spreadsheetSpecFromAttr,
} from "./spreadsheetHtml";

function SpreadsheetNodeView({ node }: ReactNodeViewProps) {
  const spec = node.attrs.spec as SpreadsheetSpec | null;
  if (!spec) {
    return (
      <NodeViewWrapper className="my-3">
        <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
          Unrenderable spreadsheet
        </div>
      </NodeViewWrapper>
    );
  }
  return (
    <NodeViewWrapper className="my-3">
      <div className="overflow-x-auto rounded-lg border border-border bg-background p-3">
        {spec.sheets.map((sheet, si) => (
          <div key={si} className={si > 0 ? "mt-4" : undefined}>
            {spec.sheets.length > 1 && (
              <p className="mb-2 text-sm font-semibold">{sheet.name}</p>
            )}
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {sheet.columns.map((c) => (
                    <th
                      key={c.key}
                      className="border border-border bg-muted/40 px-2 py-1 text-left font-semibold"
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sheet.rows.map((row, ri) => (
                  <tr key={ri}>
                    {sheet.columns.map((c) => {
                      const v = row[c.key];
                      return (
                        <td
                          key={c.key}
                          className="border border-border px-2 py-1 align-top"
                        >
                          {v === null || v === undefined ? "" : String(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </NodeViewWrapper>
  );
}

export const SpreadsheetNode = Node.create({
  name: "spreadsheetBlock",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      spec: {
        default: null,
        parseHTML: (el: HTMLElement) =>
          spreadsheetSpecFromAttr(
            el.getAttribute("data-morgan-spreadsheet") ?? "",
          ),
        renderHTML: (attrs: { spec: SpreadsheetSpec | null }) =>
          attrs.spec
            ? { "data-morgan-spreadsheet": encodeSpreadsheetAttr(attrs.spec) }
            : {},
      },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-morgan-spreadsheet]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(SpreadsheetNodeView);
  },
});
